/** DEMO ONLY — View 3: price book grid with dynamic band columns. */
import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Switch,
  Button,
  Tooltip,
} from "@mui/material";
import { useDemo, bandsForPrice } from "../demoStore";
import { SPORTS, ITEMS, FABRICS } from "../demoData";
import { formatCents } from "../resolver";

const itemName = (id) => ITEMS.find((i) => i.id === id)?.name ?? "?";
const fabricName = (id) => FABRICS.find((f) => f.id === id)?.name ?? "?";

export default function PriceGridView() {
  const { state, dispatch } = useDemo();
  const [sportId, setSportId] = useState(1);

  const rows = state.prices
    .filter((p) => p.sportId === sportId)
    .filter((p) => state.showInactive || p.status === "active");

  const columns = state.globalBands;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Price book grid
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Sports Category"
          value={sportId}
          onChange={(e) => setSportId(Number(e.target.value))}
          sx={{ minWidth: 200 }}
        >
          {SPORTS.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={state.showInactive}
              onChange={(e) =>
                dispatch({ type: "SET_SHOW_INACTIVE", value: e.target.checked })
              }
            />
          }
          label="Show inactive"
        />
      </Box>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#D5F9FC" }}>
              <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Fabric</TableCell>
              {columns.map((c) => (
                <TableCell key={c.label} sx={{ fontWeight: 600 }} align="right">
                  {c.label}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }} align="right">
                FOB
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 3}>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No price rows for this category.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => {
              const custom = !!p.presetName;
              const bands = bandsForPrice(p, state.globalBands);
              return (
                <TableRow
                  key={p.id}
                  sx={{ opacity: p.status === "inactive" ? 0.45 : 1 }}
                >
                  <TableCell>
                    {itemName(p.itemId)}
                    {p.status === "inactive" && (
                      <Chip label="inactive" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{fabricName(p.fabricId)}</TableCell>

                  {custom ? (
                    <TableCell colSpan={columns.length} sx={{ py: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title={`Bands: ${p.presetName}`}>
                          <Chip
                            label={`custom bands · ${p.presetName}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                        {bands.map((b) => (
                          <span key={b.label} style={{ fontSize: 13 }}>
                            <strong>{b.label}:</strong>{" "}
                            {p.valuesCents[b.label] != null
                              ? formatCents(p.valuesCents[b.label])
                              : "—"}
                          </span>
                        ))}
                      </Box>
                    </TableCell>
                  ) : (
                    columns.map((c) => (
                      <TableCell key={c.label} align="right">
                        {p.valuesCents[c.label] != null ? (
                          formatCents(p.valuesCents[c.label])
                        ) : (
                          <Tooltip title="No price set for this band">
                            <span style={{ color: "#D32F2F" }}>—</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    ))
                  )}

                  <TableCell align="right">{formatCents(p.fobCents)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {rows.some((p) => p.status === "inactive") || state.showInactive ? (
        <Box sx={{ mt: 1 }}>
          {rows
            .filter((p) => p.status === "inactive")
            .map((p) => (
              <Button
                key={p.id}
                size="small"
                onClick={() =>
                  dispatch({ type: "TOGGLE_PRICE_STATUS", id: p.id })
                }
              >
                Reactivate {itemName(p.itemId)} / {fabricName(p.fabricId)}
              </Button>
            ))}
        </Box>
      ) : null}
    </Box>
  );
}
